// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */
import * as React from 'react';
import { OverlayPortal } from '@ta/lithium.portals';
import EventBoundary from '@ta/events.event-boundary';
import KeyboardEventListener from '@ta/events.keyboard-event-listener';
import { FocusTrap } from '@ta/a11y.focus';
import Backdrop from './Backdrop';
import useBlockScroll from './useBlockScroll';
import styles from './ModalBase.css';

/**
 * A generic base to serve for other modals with the following properties:
 *
 * 1. Renders a backdrop which blocks user interaction in the background
 * 2. Disables scrolling of the page content while open
 * 3. Manages accessibility; Traps focus to the modal itself and automatically focuses on open. Returns focus to outer modals on close
 *    Opening/closing modals should prompt the appropriate accessibility actions and should be closable by certain keyboard events (Escape)
 */

type Props = {
  /** If true, focus will never automatically shift focus or be managed */
  disableAutoFocus?: boolean,
  /** If true, scrolling will not be blocked */
  disableBlockScroll?: boolean,
  /** If true, no backdrop will be rendered. This value should be false so there is always a visual indication that interaction is blocked */
  hideBackdrop?: boolean,
  /** If true, events will not bubble up out of the modal */
  enableEventBoundary?: boolean,
  /** A callback fired to indicate the modal should close */
  onClose?: () => mixed,
  /** The color of the backdrop */
  backdropShade?: 'light' | 'dark',
  /** The content node of the modal */
  children: React.Node,
  /** Any other props are forwarded to the container */
  [key: string]: any,
};

type OptionalProps = {|
  condition: mixed,
  wrap: (children: React.Node) => React.Node,
  children: React.Node,
|};

const Optional = ({ condition, wrap, children }: OptionalProps) => {
  if (condition) {
    return wrap(children);
  }
  return children;
};

const ModalBase = React.forwardRef<Props, _>(
  (
    {
      disableAutoFocus,
      disableBlockScroll,
      enableEventBoundary,
      onClose,
      hideBackdrop,
      backdropShade,
      children,
      ...forwardedProps
    },
    ref,
  ) => {
    useBlockScroll(!disableBlockScroll);
    return (
      <Optional
        wrap={el => (
          <EventBoundary events={['onClick']} className={styles.event_boundary}>
            {el}
          </EventBoundary>
        )}
        condition={enableEventBoundary}
      >
        <Optional
          wrap={el => (
            <Backdrop onClick={onClose} backdropShade={backdropShade}>
              {el}
            </Backdrop>
          )}
          condition={!hideBackdrop}
        >
          <OverlayPortal {...forwardedProps} ref={ref}>
            <Optional wrap={el => <FocusTrap>{el}</FocusTrap>} condition={!disableAutoFocus}>
              {children}
            </Optional>
          </OverlayPortal>
          {onClose && (
            <KeyboardEventListener
              keyName="Escape"
              callback={() => {
                onClose();
              }}
            />
          )}
        </Optional>
      </Optional>
    );
  },
);
ModalBase.displayName = 'ModalBase';

export default ModalBase;
